import React from 'react';
import type { UserGroup } from '@/pages/MeetingDashboard/MeetingDashboard';

interface CoachingModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  error: string | null;
  coachData: any;
  group: UserGroup;
}

const CoachingModal: React.FC<CoachingModalProps> = ({ open, onClose, loading, error, coachData, group }) => {
  if (!open) return null;
  const insights = coachData?.insights;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(16px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        width: '100%', maxWidth: 620, maxHeight: '88vh',
        display: 'flex', flexDirection: 'column', borderRadius: 24, overflow: 'hidden',
        background: 'linear-gradient(160deg, #11101d 0%, #0d0c18 60%, #090912 100%)',
        border: '1px solid rgba(124,112,232,0.25)',
        boxShadow: '0 40px 100px -10px rgba(0,0,0,0.9), 0 0 50px rgba(124,112,232,0.12)',
      }}>
        <div style={{ padding: '22px 24px 16px', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 14, flexShrink: 0,
            background: 'linear-gradient(135deg, rgba(184,248,144,0.15), rgba(184,248,144,0.05))',
            border: '1px solid rgba(184,248,144,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B8F890" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(184,248,144,0.6)', margin: '0 0 2px', fontWeight: 700 }}>
              AI Coaching Intelligence
            </p>
            <p style={{ fontSize: 16, fontWeight: 800, color: '#fff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {group.userName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
              cursor: 'pointer', color: '#fff',
            }}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', flexShrink: 0 }}/>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.08) transparent' }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: 16 }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', border: '2px solid rgba(184,248,144,0.2)', borderTop: '2px solid #B8F890', animation: 'spin 1s linear infinite' }}/>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 16, fontWeight: 600, color: '#fff', margin: '0 0 6px' }}>Generating Insights</p>
                <p style={{ fontSize: 14, color: '#fff', margin: 0 }}>Analysing pitches & patterns… (30–40s)</p>
              </div>
            </div>
          )}
          {!loading && error && (
            <div style={{
              borderRadius: 14, border: '1px solid rgba(248,113,113,0.25)',
              background: 'rgba(248,113,113,0.06)', padding: '16px 20px',
            }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#fca5a5', margin: '0 0 4px' }}>Generation Failed</p>
              <p style={{ fontSize: 14, color: 'rgba(252,165,165,0.7)', margin: 0 }}>{error}</p>
            </div>
          )}
          {!loading && !error && insights && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {(insights.pitchAnalysis || insights.overallAssessment) && (
                <div style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.08), rgba(167,139,250,0.02))', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 16, padding: '16px 20px' }}>
                  <p style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, color: '#a78bfa', margin: '0 0 8px' }}>Pitch Analysis</p>
                  <p style={{ fontSize: 14, color: '#e5e7eb', margin: 0, lineHeight: 1.6 }}>{insights.pitchAnalysis || insights.overallAssessment}</p>
                </div>
              )}
              {insights.meetingAnalysis && (
                <div style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.08), rgba(167,139,250,0.02))', border: '1px solid rgba(167,139,250,0.2)', borderRadius: 16, padding: '16px 20px' }}>
                  <p style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, color: '#a78bfa', margin: '0 0 8px' }}>Meeting Analysis</p>
                  <p style={{ fontSize: 14, color: '#e5e7eb', margin: 0, lineHeight: 1.6 }}>{insights.meetingAnalysis}</p>
                </div>
              )}
              {insights.quickWin && (
                <div style={{ background: 'linear-gradient(135deg, rgba(52,211,153,0.08), rgba(52,211,153,0.02))', border: '1px solid rgba(52,211,153,0.2)', borderRadius: 16, padding: '16px 20px' }}>
                  <p style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, color: '#34d399', margin: '0 0 8px' }}>⚡ Quick Win</p>
                  <p style={{ fontSize: 14, color: '#e5e7eb', margin: 0, lineHeight: 1.6 }}>{insights.quickWin}</p>
                </div>
              )}
              {insights.managerTip && (
                <div style={{ background: 'linear-gradient(135deg, rgba(56,189,248,0.08), rgba(56,189,248,0.02))', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 16, padding: '16px 20px' }}>
                  <p style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, color: '#38bdf8', margin: '0 0 8px' }}>👤 Manager Tip</p>
                  <p style={{ fontSize: 14, color: '#e5e7eb', margin: 0, lineHeight: 1.6 }}>{insights.managerTip}</p>
                </div>
              )}
              {insights.objectionCoaching && (
                <div style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.02))', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 16, padding: '16px 20px' }}>
                  <p style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, color: '#f59e0b', margin: '0 0 8px' }}>🛡 Objection Coaching</p>
                  <p style={{ fontSize: 14, color: '#e5e7eb', margin: 0, lineHeight: 1.6 }}>{insights.objectionCoaching}</p>
                </div>
              )}
              {Array.isArray(insights.priorityFocusAreas) && insights.priorityFocusAreas.length > 0 && (
                <div>
                  <p style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, color: '#fff', margin: '0 0 12px' }}>Priority Focus Areas</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {insights.priorityFocusAreas.map((fa: any, idx: number) => {
                      const cols = ['#a78bfa', '#38bdf8', '#f59e0b'];
                      const col  = cols[idx % cols.length];
                      return (
                        <div key={idx} style={{ background: `linear-gradient(135deg, ${col}08, ${col}03)`, border: `1px solid ${col}25`, borderRadius: 14, padding: '14px 18px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: fa.why || fa.specificAction ? 10 : 0 }}>
                            <span style={{ width: 26, height: 26, borderRadius: '50%', background: `${col}20`, color: col, border: `1px solid ${col}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{idx + 1}</span>
                            <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0, textTransform: 'capitalize' }}>{fa.area}</p>
                          </div>
                          {fa.why && <p style={{ fontSize: 14, color: '#fff', margin: '0 0 8px', paddingLeft: 36, lineHeight: 1.5 }}><span style={{ color: `${col}80`, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginRight: 6 }}>Why</span>{fa.why}</p>}
                          {fa.specificAction && <div style={{ background: `${col}08`, border: `1px solid ${col}15`, borderRadius: 10, padding: '10px 14px', marginLeft: 36 }}><p style={{ fontSize: 14, color: '#d1d5db', margin: 0, lineHeight: 1.5 }}><span style={{ color: col, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginRight: 6 }}>Action</span>{fa.specificAction}</p></div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {!loading && !error && !insights && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <p style={{ fontSize: 14, color: '#fff', margin: 0 }}>No insights available yet.</p>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#B8F890' }}/>
            <span style={{ fontSize: 14, textTransform: 'uppercase',  color: '#fff', fontWeight: 600 }}>Powered by AI · Pitchster</span>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', color: '#fff', padding: '7px 18px', borderRadius: 999, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoachingModal;
